import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { useFamilyStore } from '../../stores/familyStore';
import { useAuthStore } from '../../stores/authStore';
import { useApiService, type FamilyInvitation } from '../../services/api';
import { PrimaryParentOnly } from '../security/PermissionGate';

/**
 * Komponent wyświetlający listę zaproszeń dla rodziny
 */
export const InvitationsList: React.FC = () => {
  const { user } = useAuthStore();
  const { family } = useFamilyStore();
  const [invitations, setInvitations] = useState<FamilyInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setUserRole] = useState<string>(''); // userRole - będzie używane w przyszłych wersjach
  
  const apiService = useApiService();

  const fetchInvitations = async () => {
    if (!family || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      logger.debug('🔍 Pobieranie zaproszeń przez nowe API...');
      const result = await apiService.getInvitations(family.id);
      setInvitations(result.invitations);
      setUserRole(result.userRole);
      logger.debug('✅ Zaproszenia pobrane:', result);
    } catch (err: any) {
      console.error('❌ Błąd pobierania zaproszeń:', err);
      setError(err.message || 'Nie udało się pobrać zaproszeń');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
    // Auto-odświeżenie co 15s w tle (np. gdy zaproszenia zmienią status)
    const interval = setInterval(() => {
      fetchInvitations();
    }, 15000);
    return () => clearInterval(interval);
  }, [family, user]);

  const handleCancelInvitation = async (invitationId: string) => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiService.cancelInvitation(invitationId);
      
      if (result.success) {
        // Odśwież listę zaproszeń po anulowaniu
        await fetchInvitations();
      }
    } catch (err: any) {
      console.error('❌ Błąd anulowania zaproszenia:', err);
      setError(err.message || 'Nie udało się anulować zaproszenia');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-warning">Oczekujące</span>;
      case 'accepted':
        return <span className="badge badge-success">Zaakceptowane</span>;
      case 'rejected':
        return <span className="badge badge-error">Odrzucone</span>;
      case 'expired':
        return <span className="badge badge-ghost">Wygasło</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const isExpired = (expiresAt: string) => {
    const expiryDate = new Date(expiresAt);
    return expiryDate < new Date();
  };

  // Funkcja do wyświetlania nazwy roli
  const getRoleDisplayName = (role: string): string => {
    switch (role) {
      case 'parent_a':
        return 'Rodzic / Opiekun (Administrator)';
      case 'parent_b':
        return 'Rodzic / Opiekun';
      case 'child_viewer':
        return 'Obserwator';
      default:
        return role;
    }
  };

  // Generuj link zaproszenia
  const getInvitationUrl = (invitation: FamilyInvitation): string => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invitation/${invitation.token}`;
  };

  return (
    <PrimaryParentOnly fallback={
      <div className="text-center py-4 text-gray-500">
        Tylko administrator może zarządzać zaproszeniami
      </div>
    }>
      <div>
        <h2 className="text-lg font-semibold mb-4">Wysłane zaproszenia</h2>

        {isLoading ? (
          <div className="text-center py-4">Ładowanie zaproszeń...</div>
        ) : error ? (
          <div className="text-center py-4 text-error">{error}</div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-4 text-gray-500">Brak wysłanych zaproszeń</div>
        ) : (
          <div className="space-y-4">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className={`border rounded-md p-3 ${
                  isExpired(invitation.expires_at) && invitation.status === 'pending'
                    ? 'bg-gray-50'
                    : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{invitation.email}</div>
                    <div className="text-sm text-gray-500">
                      Rola: {getRoleDisplayName(invitation.role)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Wysłano: {new Date(invitation.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-500">
                      Wygasa: {new Date(invitation.expires_at).toLocaleDateString()}
                    </div>
                    {invitation.status === 'pending' && !isExpired(invitation.expires_at) && (
                      <div className="text-sm mt-2 bg-blue-50 p-2 border border-blue-100 rounded-md">
                        <div className="font-medium text-blue-800 mb-1">Link zaproszenia:</div>
                        <div className="text-blue-600 break-all text-xs font-mono bg-white p-1 rounded border mb-2">
                          {getInvitationUrl(invitation)}
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(getInvitationUrl(invitation));
                            // TODO: Dodać toast notification
                            alert('Link skopiowany do schowka!');
                          }}
                          className="btn btn-xs btn-outline btn-primary"
                        >
                          📋 Kopiuj link
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end">
                    {getStatusLabel(
                      isExpired(invitation.expires_at) && invitation.status === 'pending'
                        ? 'expired'
                        : invitation.status
                    )}
                    
                    {invitation.status === 'pending' && !isExpired(invitation.expires_at) && (
                      <button
                        onClick={() => handleCancelInvitation(invitation.id)}
                        className="btn btn-xs btn-outline btn-error mt-2"
                        disabled={isLoading}
                      >
                        Anuluj
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PrimaryParentOnly>
  );
};